import {
  DomainTuple,
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
} from 'victory';
import {
  IndicatorSingleValue,
  MarketCipherWaves,
} from '../../../screens/ProfitCalculations/types';
import { useMemo } from 'react';
import { XAxisInfo } from '../../MultiChart';
import { colors } from '../../../colors';
import IndicatorLabel from '../IndicatorLabel';

interface Props {
  waves: MarketCipherWaves | undefined;

  width?: number | undefined;
  height?: number | undefined;
  xAxisInfo?: XAxisInfo | undefined;
  showXAxis?: boolean;
}

type DataPoint = {
  x: number;
  y: number;
  color?: string;
};

const transformToDataPoints = (
  data: IndicatorSingleValue[] | undefined,
): DataPoint[] => {
  if (!data?.length || data.length < 2) {
    return [];
  }

  /** Because the candles start at the beginning of the candle
   * and the indicator values is al the end of the candle
   * We have to change position a little
   */
  const range =
    data[data.length - 1].timestamp - data[data.length - 2].timestamp;
  return data.map((d) => ({ x: d.timestamp - range, y: d.value })) ?? [];
};

const MarketCipherWavesComponent = (props: Props) => {
  const { waves, width, height, xAxisInfo, showXAxis } = props;

  const whiteWaveDataPoints = useMemo(
    () =>
      transformToDataPoints(waves?.wave1).filter(
        (d) => d.x >= (xAxisInfo?.domain.x[0] ?? 0),
      ),
    [waves?.wave1, xAxisInfo?.domain.x],
  );

  const blueWaveDataPoints = useMemo(
    () =>
      transformToDataPoints(waves?.wave2).filter(
        (d) => d.x >= (xAxisInfo?.domain.x[0] ?? 0),
      ),
    [waves?.wave2, xAxisInfo?.domain.x],
  );

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: '#999' },
    }),
    [],
  );

  const domain = useMemo(() => {
    if (!xAxisInfo?.domain.x) {
      return undefined;
    }

    const x = xAxisInfo.domain.x;
    const y = [-110, 115] as DomainTuple;

    return { x, y };
  }, [xAxisInfo?.domain.x]);

  const gradientMiddleOffset = useMemo(() => {
    const max = Math.max(
      ...whiteWaveDataPoints.map((d) => d.y),
      ...blueWaveDataPoints.map((d) => d.y),
    );
    const min = Math.min(
      ...whiteWaveDataPoints.map((d) => d.y),
      ...blueWaveDataPoints.map((d) => d.y),
    );
    const domainYRange = max - min;
    const middleOffset = max / domainYRange;
    return middleOffset;
  }, [blueWaveDataPoints, whiteWaveDataPoints]);

  const topGradientOpacity = useMemo(() => {
    const topGradientThreshold = 75;
    const y1 = Math.max(
      ...whiteWaveDataPoints.map((d) => d.y),
      ...blueWaveDataPoints.map((d) => d.y),
    );

    const percentage = (topGradientThreshold - y1) / topGradientThreshold;
    const opacity = 0.75 - (0.75 - 0.6) * percentage;

    return opacity;
  }, [blueWaveDataPoints, whiteWaveDataPoints]);

  const bottomGradientOpacity = useMemo(() => {
    const bottomGradientThreshold = -75;
    const y0 = Math.min(
      ...whiteWaveDataPoints.map((d) => d.y),
      ...blueWaveDataPoints.map((d) => d.y),
    );

    const percentage = (bottomGradientThreshold - y0) / bottomGradientThreshold;
    const opacity = 0.75 - (0.75 - 0.6) * percentage;

    return opacity;
  }, [blueWaveDataPoints, whiteWaveDataPoints]);

  const chartWidth = width;
  const chartHeight = height || 120;

  return (
    <div
      className="candlesticks-container"
      style={{
        width: chartWidth,
        height: chartHeight,
      }}
    >
      <IndicatorLabel label={'Market Cipher Waves'} />
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop
              offset="0"
              stopColor={colors.green}
              stopOpacity={topGradientOpacity}
            />
            <stop
              offset={gradientMiddleOffset}
              stopColor={colors.green}
              stopOpacity={0.6}
            />
            <stop
              offset={gradientMiddleOffset}
              stopColor={colors.red}
              stopOpacity={0.6}
            />
            <stop
              offset="1"
              stopColor={colors.red}
              stopOpacity={bottomGradientOpacity}
            />
          </linearGradient>
        </defs>
      </svg>
      <VictoryChart
        width={chartWidth}
        domain={domain}
        domainPadding={xAxisInfo?.domainPadding}
        height={height}
        padding={{
          ...xAxisInfo?.chartPadding,
          top: 0,
          bottom: showXAxis ? 30 : 0,
        }}
      >
        <VictoryAxis
          tickFormat={showXAxis ? xAxisInfo?.tickFormat : () => ''}
          style={{ ...xAxisInfo?.axisStyle, ...(!showXAxis && { ticks: {} }) }}
          tickValues={xAxisInfo?.tickValues}
          tickLabelComponent={<VictoryLabel dy={chartHeight / 2.5} />}
        />
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          orientation={'right'}
          tickLabelComponent={<VictoryLabel dx={5} />}
          tickValues={[-100, -50, 0, 50, 100]}
          tickFormat={(t) => `${t}%`}
        />

        <VictoryArea
          data={whiteWaveDataPoints}
          style={{
            data: { fill: colors.white },
          }}
        />
        <VictoryArea
          data={blueWaveDataPoints}
          style={{
            data: { fill: 'url(#gradient)' },
          }}
        />
      </VictoryChart>
    </div>
  );
};

export default MarketCipherWavesComponent;
